import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../store/snackbarSlice';
import { Avatar, Box, IconButton, LinearProgress, Paper, Slide, Stack, Tooltip, Typography, alpha, useMediaQuery } from '@mui/material';
import CallEndRounded from '@mui/icons-material/CallEndRounded';
import CallRounded from '@mui/icons-material/CallRounded';
import { useAppSelector } from '../../hooks/useAppStore';
import { getPusher } from '../../lib/pusher';
import { useRespondCallMutation, liveSessionsApi } from '../../store/api/liveSessionsApi';
import { chatCallsApi, useRespondChatCallMutation, LLAMADA_ACTIVA } from '../../store/api/chatCallsApi';
import { useFeatureFlag, useFlagEstricta } from '../../hooks/useFeatureFlag';
import { ORO, VERDE, latido } from './estilo';
import { sonarTonoDeLlamada } from './tonoDeLlamada';

/** `origen`: una sesión de Academy, o una llamada del chat (de a dos o de grupo). */
interface Timbre { origen: 'academy' | 'chat'; callId: string; sessionId: string; conversationId?: string; title: string; hostName: string; expiresAt: string }
interface TimbreDelChat { callId: string; conversationId: string; kind: 'direct' | 'group'; title: string; fromName: string; expiresAt: string }

/**
 * EL TIMBRE DE LA LLAMADA INTERNA (fase 3, 01/10/2026). Vive en el layout del CRM: escucha
 * `live_call.ring` en el canal `private-user-{id}` (el mismo que ya usan el chat y los avisos) y
 * muestra "Te está llamando…" con Aceptar o Cancelar. Aceptar lleva a la sala; cancelar avisa al
 * anfitrión. Si pasa el tiempo, se cierra solo.
 */
export function LlamadaEnVivo() {
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  // El timbre sirve a Academy (`en-vivo`) y a las llamadas del chat (`llamadas-chat`, estricta).
  const conAcademy = useFeatureFlag('en-vivo');
  const conLlamadasDelChat = useFlagEstricta('llamadas-chat');
  const habilitado = conAcademy || conLlamadasDelChat;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [responder] = useRespondCallMutation();
  const [responderChat] = useRespondChatCallMutation();
  const myId = useAppSelector((s) => s.auth.user?._id);
  const [timbre, setTimbre] = useState<Timbre | null>(null);
  const [restante, setRestante] = useState(100);
  const suscripto = useRef(false);
  const menosMovimiento = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEffect(() => {
    if (!accessToken || !habilitado || suscripto.current) return;
    let userId: string;
    try { userId = JSON.parse(atob(accessToken.split('.')[1])).sub as string; } catch { return; }
    const channel = getPusher(accessToken).subscribe(`private-user-${userId}`);
    const onRing = (data: unknown) => setTimbre({ ...(data as Omit<Timbre, 'origen'>), origen: 'academy' });
    // La del chat usa el mismo timbre: "Fulano te está llamando" o "Fulano llama a <grupo>".
    const onRingChat = (d: TimbreDelChat) => setTimbre({
      origen: 'chat', callId: d.callId, sessionId: '', conversationId: d.conversationId,
      hostName: d.fromName, title: d.kind === 'group' ? `Llamada al grupo ${d.title}` : 'Llamada del chat', expiresAt: d.expiresAt,
    });
    channel.bind('live_call.ring', onRing);
    channel.bind('chat_call.ring', onRingChat);
    suscripto.current = true;
    return () => { channel.unbind('live_call.ring', onRing); channel.unbind('chat_call.ring', onRingChat); suscripto.current = false; };
  }, [accessToken, habilitado]);

  // Llamada del chat que dejó de sonarme (la atendí en otro dispositivo, la cortó el que llamaba,
  // la rechacé desde el teléfono): se apaga el timbre acá también.
  useEffect(() => {
    if (!accessToken || !habilitado || timbre?.origen !== 'chat') return;
    let userId: string;
    try { userId = JSON.parse(atob(accessToken.split('.')[1])).sub as string; } catch { return; }
    const channel = getPusher(accessToken).subscribe(`private-user-${userId}`);
    const callId = timbre.callId;
    const onUpdate = async (d: { callId: string }) => {
      if (d.callId !== callId) return;
      try {
        const c = await dispatch(chatCallsApi.endpoints.getChatCall.initiate(callId, { forceRefetch: true }) as never) as unknown as { data?: { participants: { user: string; status: string }[] } };
        const yo = c.data?.participants.find((p) => p.user === myId);
        if (!yo || yo.status !== 'ringing') setTimbre((t) => (t?.callId === callId ? null : t));
      } catch { /* si falla, el timbre igual se cierra al vencer */ }
    };
    channel.bind('chat_call.updated', onUpdate);
    return () => { channel.unbind('chat_call.updated', onUpdate); };
  }, [accessToken, habilitado, timbre?.origen, timbre?.callId, myId, dispatch]);

  // El tono suena mientras está el timbre y se corta al atender, cortar o vencer.
  useEffect(() => {
    if (!timbre) return;
    return sonarTonoDeLlamada();
  }, [timbre]);

  useEffect(() => {
    if (!timbre) return;
    const vence = new Date(timbre.expiresAt).getTime(), inicio = Date.now();
    const t = setInterval(() => {
      const r = Math.max(0, (vence - Date.now()) / Math.max(1, vence - inicio)) * 100;
      setRestante(r);
      if (r <= 0) setTimbre(null);
    }, 250);
    return () => clearInterval(t);
  }, [timbre]);

  // "Atender" desde la push del teléfono: el service worker abre el CRM en `?atender=<callId>`.
  // Se saca de la URL y se atiende como si hubiera tocado el botón verde.
  const location = useLocation();
  const atendiendo = useRef<string | null>(null);
  useEffect(() => {
    const callId = new URLSearchParams(location.search).get('atender');
    if (!callId || !accessToken || !habilitado || atendiendo.current === callId) return;
    atendiendo.current = callId;
    const q = new URLSearchParams(location.search); q.delete('atender');
    const deChat = q.get('tipo') === 'chat'; q.delete('tipo');
    navigate({ pathname: location.pathname, search: q.toString() ? `?${q}` : '' }, { replace: true });
    setTimbre(null);
    void (deChat ? entrarAlChat(callId) : entrar(callId));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, accessToken, habilitado]);

  const entrar = async (callId: string) => {
    try {
      const r = await responder({ callId, acepta: true }).unwrap();
      if (r.status === 'accepted') {
        dispatch(liveSessionsApi.util.invalidateTags([{ type: 'LiveSession', id: 'MINE' }]));
        navigate(`/en-vivo/${r.sessionId}`);
      } else dispatch(showSnackbar({ message: r.status === 'expired' ? 'La llamada ya se cortó. Pedile que te vuelva a llamar.' : 'La llamada ya no está disponible', severity: 'warning' }));
    } catch {
      dispatch(showSnackbar({ message: 'No se pudo atender la llamada. Pedile que te vuelva a llamar.', severity: 'error' }));
    }
  };

  /** Atender una llamada del chat: quedo adentro y voy a la charla (la barra de la llamada la muestra LlamadaDelChat). */
  const entrarAlChat = async (callId: string) => {
    try {
      const c = await responderChat({ callId, acepta: true }).unwrap();
      const yo = c.participants.find((p) => p.user === myId);
      if (yo?.status === 'joined') {
        dispatch(chatCallsApi.util.invalidateTags([LLAMADA_ACTIVA]));
        navigate(`/chat?conv=${c.conversationId}`);
      } else dispatch(showSnackbar({ message: c.status === 'ringing' || c.status === 'ongoing' ? 'La llamada ya no está disponible' : 'La llamada ya se cortó', severity: 'warning' }));
    } catch (e) {
      const msg = (e as { data?: { message?: string } })?.data?.message;
      dispatch(showSnackbar({ message: msg ?? 'No se pudo atender la llamada. Pedile que te vuelva a llamar.', severity: 'error' }));
    }
  };

  const contestar = async (acepta: boolean) => {
    if (!timbre) return;
    const t = timbre; setTimbre(null);
    if (t.origen === 'chat') {
      if (acepta) await entrarAlChat(t.callId);
      else await responderChat({ callId: t.callId, acepta: false }).unwrap().catch(() => undefined);
      return;
    }
    // Solo se entra a la sala si el backend registró el "sí": sin eso no quedás invitado y la
    // sala te rebota con "No estás invitado". Si falla o se venció, se avisa y no se navega.
    try {
      const r = await responder({ callId: t.callId, acepta }).unwrap();
      if (acepta && r.status === 'accepted') {
        // Ahora está invitado: lo que el CRM tenía guardado de "En vivo" ya no vale.
        dispatch(liveSessionsApi.util.invalidateTags([{ type: 'LiveSession', id: 'MINE' }]));
        navigate(`/en-vivo/${t.sessionId}`);
      }
      else if (acepta) dispatch(showSnackbar({ message: 'La llamada ya no está disponible', severity: 'warning' }));
    } catch {
      if (acepta) dispatch(showSnackbar({ message: 'No se pudo atender la llamada. Pedile que te vuelva a llamar.', severity: 'error' }));
    }
  };

  const t = timbre;
  return (
    /**
     * ⛔ ARRIBA, COMO LA BARRA DE MENSAJES DE GUSTAVO (01/10/2026: "no abajo: en la barra de arriba,
     * con el mismo estilo que cuando Gustavo te manda un mensaje, pero con cortar y atender").
     * Misma pastilla oscura con borde del acento, avatar a la izquierda, título en el acento y
     * subtítulo; en vez de la X, los dos botones. La barra fina de abajo es el tiempo que queda.
     */
    <Slide in={!!t} direction="down" mountOnEnter unmountOnExit appear>
      <Paper role="alertdialog" aria-labelledby="timbre-titulo" aria-describedby="timbre-texto" elevation={0}
        sx={{
          position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: (th) => th.zIndex.snackbar + 3,
          display: 'flex', alignItems: 'center', gap: 1.25, pl: 1, pr: 0.75, py: 0.75, borderRadius: 999,
          width: 'max-content', maxWidth: 'min(94vw, 520px)', overflow: 'hidden',
          bgcolor: alpha('#0f1218', 0.97), color: '#fff',
          border: `1px solid ${alpha(ORO, 0.55)}`,
          boxShadow: `0 14px 44px ${alpha('#000', 0.5)}, 0 0 0 1px ${alpha(ORO, 0.08)}`, backdropFilter: 'blur(8px)',
        }}>
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Avatar sx={{ width: 40, height: 40, fontSize: 15, fontWeight: 800, bgcolor: alpha(ORO, 0.2), color: '#fff', border: `1.5px solid ${alpha(ORO, 0.5)}`, animation: menosMovimiento ? 'none' : `${latido} 1.6s ease-out infinite` }}>
            {t?.hostName.slice(0, 1)}
          </Avatar>
          <Box sx={{ position: 'absolute', right: -2, bottom: -2, width: 16, height: 16, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: VERDE, border: '2px solid #0f1218' }}>
            <CallRounded sx={{ fontSize: 9, color: '#0b0f14' }} />
          </Box>
        </Box>
        <Box sx={{ minWidth: 0, py: 0.25, mr: 0.5 }}>
          <Typography id="timbre-titulo" noWrap sx={{ fontWeight: 800, fontSize: 13.5, lineHeight: 1.2, color: ORO, maxWidth: 280 }}>{t?.hostName} te está llamando</Typography>
          <Typography id="timbre-texto" noWrap sx={{ fontSize: 12, lineHeight: 1.3, color: alpha('#fff', 0.72), maxWidth: 280 }}>{t?.title}</Typography>
        </Box>
        <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
          <Tooltip title="Cortar"><IconButton aria-label="Cortar" onClick={() => contestar(false)}
            sx={{ width: 40, height: 40, color: '#fff', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 } }}>
            <CallEndRounded sx={{ fontSize: 20 }} />
          </IconButton></Tooltip>
          <Tooltip title="Atender"><IconButton aria-label="Atender" autoFocus onClick={() => contestar(true)}
            sx={{ width: 40, height: 40, color: '#0b0f14', bgcolor: VERDE, boxShadow: `0 4px 14px ${alpha(VERDE, 0.4)}`, '&:hover': { bgcolor: '#16a34a' }, '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 } }}>
            <CallRounded sx={{ fontSize: 20 }} />
          </IconButton></Tooltip>
        </Stack>
        <LinearProgress variant="determinate" value={restante} aria-label="Tiempo para atender"
          sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, bgcolor: 'transparent', '& .MuiLinearProgress-bar': { bgcolor: ORO, transition: 'transform .25s linear' } }} />
      </Paper>
    </Slide>
  );
}
